import { useState } from 'react'

import {
  Button,
  Card,
  CardContent,
  CardHeading,
  ChartControls,
  DataTable,
  MultiLineChart,
  SectionHeading,
  TableExport,
} from '@/components/ui'
import type { ChartControlsState } from '@/components/ui/chart'
import type { Table } from '@/components/ui/table/types'
import { useProject } from '@/modules/projects'
import { semanticColors } from '@/utils'
import { incomeColumns } from './columns'
import type { IncomeChartData, IncomeTableRow } from './types'

interface IncomeListProps {
  chartData: IncomeChartData[]
  tableData: IncomeTableRow[]
}

export function IncomeList({ chartData, tableData }: IncomeListProps) {
  const project = useProject()
  const [table, setTable] = useState<Table<IncomeTableRow> | null>(null)
  const [chartControls, setChartControls] = useState<ChartControlsState | null>(
    null,
  )
  const [isCumulative, setIsCumulative] = useState(false)

  const dataDateRange = chartData.length
    ? { from: chartData[0].date, to: chartData[chartData.length - 1].date }
    : undefined

  let runningTotal = 0
  const cumulativeChartData: IncomeChartData[] = chartData.map((point) => {
    runningTotal += point.income
    return { date: point.date, income: runningTotal }
  })

  const view = isCumulative
    ? { title: 'Cumulative income trend', slug: 'cumulative-income-trend' }
    : { title: 'Monthly income trend', slug: 'monthly-income-trend' }
  const displayChartData = isCumulative ? cumulativeChartData : chartData
  const chartFilename = `${project?.slug}-${view.slug}`

  return (
    <div className="space-y-6">
      <SectionHeading
        title="Income insights"
        description="Track monthly income patterns and trends over time"
        actions={
          chartControls && (
            <ChartControls
              {...chartControls}
              filename={chartFilename}
              projectName={project?.name}
              title={view.title}
              dataDateRange={dataDateRange}
              size="md"
              floating={false}
            />
          )
        }
      />
      <Card className="w-full">
        <CardHeading
          title={view.title}
          actions={
            <Button
              theme={isCumulative ? 'default' : 'outline'}
              size="sm"
              onClick={() => setIsCumulative((prev) => !prev)}
            >
              Cumulative
            </Button>
          }
        />
        <CardContent>
          <div style={{ height: '320px' }}>
            <MultiLineChart
              data={displayChartData}
              series={[
                {
                  key: 'income',
                  label: 'Income',
                  color: semanticColors.income,
                },
              ]}
              height={320}
              legendPosition="bottom"
              interactiveLegend={false}
              onChartReady={setChartControls}
            />
          </div>
        </CardContent>
      </Card>
      <Card className="overflow-hidden">
        <CardHeading
          title="Monthly breakdown"
          description="Detailed view of income by month with percentage changes"
          className="border-b"
          actions={
            table && (
              <TableExport
                table={table}
                filename={`${project?.slug}-monthly-breakdown`}
              />
            )
          }
        />
        <DataTable
          columns={incomeColumns(
            tableData.some((row) => row.incomeFromOutside !== undefined),
          )}
          data={tableData}
          height="h-auto"
          enableRowSelection={false}
          onTableReady={setTable}
        />
      </Card>
    </div>
  )
}
